/**
 * @file auth.service.ts
 * @description Unified authentication service for Kirigami.
 * Handles Cognito email/password auth, user profile loading, token management,
 * and logout. Combines the legacy's dual-service pattern (CognitoService +
 * AuthenticationService) into a single, cleaner service.
 *
 * Uses Angular signals for reactive state and @kirigami/platform/browser for token persistence.
 *
 * @see legacy/web-console-dev/src/app/auth/services/cognito.service.ts
 * @see legacy/web-console-dev/src/app/shared/services/authentication.service.ts
 */
import { Injectable, Injector, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ReportPollingService } from './report-polling.service';
import { ToastService } from './toast.service';
import { AnalyticsService, PlanType } from './analytics.service';
import { Observable, throwError } from 'rxjs';
import { map, switchMap, catchError, tap } from 'rxjs/operators';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  ISignUpResult,
} from 'amazon-cognito-identity-js';
import {
  getScopedStorageItem,
  setScopedStorageItem,
  removeScopedStorageItem,
} from '@kirigami/platform/browser';
import { environment } from '../../environments/environment';
import {
  UserToken,
  UserAllowedSubscription,
  ActiveSubscriptionUsage,
  subscriptionDisplayLabel,
  NO_PLAN_LABEL,
  FREE_SUBSCRIPTION_ID,
} from '../models/user.model';

/**
 * Raw response shape from GET /users/get.
 * Backend returns snake_case fields; mapped to UserToken by mapToUserToken().
 */
interface UserProfileApiResponse {
  email?: string;
  name?: string;
  display_name?: string;
  role?: string;
  subscriptions?: string[];
  /** Backend snake_case. Older codepaths read `currentSubscription`; kept
   *  as an optional camelCase alias so a backend that still emits the
   *  legacy field doesn't suddenly resolve to undefined. */
  current_subscription?: string;
  currentSubscription?: string;
  available_credits?: number;
  consumed_credits?: number;
  reserved_credits?: number;
  monthly_allowance?: number;
  consumed_this_period?: number;
  period_reset_date?: string | null;
  /** Personal wallet balance in integer credits (C7). */
  credit_balance?: number;
  unread_notifications_count?: number;
  allowed_subscriptions?: Array<{
    subscription_id?: string;
    role?: string;
    owner?: string;
    status?: string;
    billing_cycle_interval?: string;
    next_billed_at?: string | null;
    price_name?: string | null;
    display_name?: string | null;
    monthly_allowance?: number;
    consumed_this_period?: number;
    period_reset_date?: string | null;
    available_tokens?: number;
    consumed_tokens?: number;
    reserved_tokens?: number;
    monthly_credit_allowance?: number;
    credits_available?: number;
    credits_consumed_this_period?: number;
    credits_reserved?: number;
    plan_credits_available?: number;
    plan_credits_consumed?: number;
  }>;
  terms_general_version?: string;
  terms_run_analysis_version?: string;
  /** One-time Free benefit consumed by a previous account (2026-07-23 spec). */
  free_plan_consumed?: boolean;
  /** 'FREE_PLAN_ALREADY_USED' while the reuse restriction is in force. */
  restriction_reason?: string | null;
}

/**
 * Response shape from POST /subscriptions/free. `granted_tokens === 0` on an
 * `activated` response (or the explicit `free_plan_consumed` flag) means the
 * one-time grant was denied — the plan is active but tokenless.
 */
interface FreePlanActivationResponse {
  status?: string;
  free_plan_activated_at?: string;
  granted_tokens?: number;
  granted_credits?: number;
  free_plan_consumed?: boolean;
  restriction_reason?: string | null;
}

/** Response shape from GET /auth/logout */
interface LogoutResponse {
  logout_url?: string;
}

/** Storage key for the access token (matches legacy convention) */
const TOKEN_KEY = 'atk';
/** Storage key for user email */
const EMAIL_KEY = 'em';
/** Storage key for display name */
const NAME_KEY = 'name';
/** Storage key for the currently selected subscription (legacy, un-scoped). */
const CSUB_KEY = 'csub';

/**
 * Per-user storage key for the currently-selected subscription. The choice is
 * namespaced by the user's email so that, on a shared browser, one user's
 * selection can never bleed into another's. Falls back to the legacy un-scoped
 * `csub` key when the email is not yet known (should not normally happen, since
 * the email is persisted before this is read). Email is lower-cased so the key
 * is stable regardless of how the address was typed/casing from the provider.
 */
export function currentSubscriptionKey(email: string | null | undefined): string {
  const normalized = (email || '').trim().toLowerCase();
  return normalized ? `${CSUB_KEY}::${normalized}` : CSUB_KEY;
}

/**
 * Subscription ids hidden from the UI (the header switcher and any list that
 * renders `allowedSubscriptions`).
 *
 * 'pay-as-you-go' is RETIRED platform-wide (2026-07-31): the backend answers
 * 403 PAY_AS_YOU_GO_RETIRED to any attempt to bill it or scope a new report
 * with it, /users/get no longer advertises it, and the dashboard/landing
 * upload pipelines fail closed (no-plan gate) instead of falling back to the
 * literal. The id survives only as the personal-wallet pool name server-side
 * and as the frozen storage scope of pre-cutover reports. This entry stays as
 * defence-in-depth against stale backend responses.
 */
const HIDDEN_SUBSCRIPTION_IDS = new Set<string>(['pay-as-you-go']);

/** BroadcastChannel name used to sync logout events across browser tabs. */
const AUTH_CHANNEL_NAME = 'kirigami-auth';

/**
 * Prefix used by the Report Detail surface to persist in-progress section
 * drafts to sessionStorage (see
 * docs/specs/2026-05-21-report-detail-drafts-session-persistence-design.md).
 * Kept in sync with `report-detail.store.ts`. A mismatch here means
 * stale drafts leak across user sessions on the same browser tab.
 */
const REPORT_DRAFT_STORAGE_PREFIX = 'kirigami.reportDetail.drafts.v1.';

/**
 * Removes every Report Detail draft persisted in sessionStorage. Called
 * by both `AuthService.logout()` (intentional sign-out) and
 * `AuthService.handleSessionExpired()` (silent 401-driven logout) so a
 * new user signing in on the same tab never inherits the previous
 * user's drafts.
 *
 * Exported (rather than a private method) so tests can drive it
 * directly without constructing AuthService — the existing spec avoids
 * the Cognito-laden constructor by convention.
 *
 * Silent no-op when sessionStorage is unavailable (SSR / private-mode).
 */
export function purgeReportDraftStorage(): void {
  if (typeof window === 'undefined') return;
  let storage: Storage | undefined;
  try {
    storage = window.sessionStorage;
  } catch {
    return;
  }
  if (!storage) return;

  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k && k.startsWith(REPORT_DRAFT_STORAGE_PREFIX)) keys.push(k);
  }
  for (const k of keys) storage.removeItem(k);
}

/**
 * Prefix used by the References surfaces to persist per-user, per-tab
 * search history (see
 * docs/specs/2026-05-21-references-search-history-design.md).
 * Kept in sync with `ref-search-history.service.ts`.
 */
const REF_SEARCH_HISTORY_STORAGE_PREFIX = 'kirigami.references.searchHistory.v1.';

/**
 * Removes every References search-history entry persisted in
 * sessionStorage. Called by both `AuthService.logout()` and
 * `AuthService.handleSessionExpired()` so a new user signing in on the
 * same browser tab never inherits the previous user's history.
 *
 * Silent no-op when sessionStorage is unavailable (SSR / private-mode).
 */
export function purgeRefSearchHistoryStorage(): void {
  if (typeof window === 'undefined') return;
  let storage: Storage | undefined;
  try {
    storage = window.sessionStorage;
  } catch {
    return;
  }
  if (!storage) return;

  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k && k.startsWith(REF_SEARCH_HISTORY_STORAGE_PREFIX)) keys.push(k);
  }
  for (const k of keys) storage.removeItem(k);
}

/**
 * Prefix used by the references-panel KB session cache (see
 * docs/superpowers/specs/2026-05-26-kb-session-cache-design.md).
 * Kept in sync with `knowledge-base.service.ts`.
 */
const KB_CACHE_STORAGE_PREFIX = 'kirigami.kb.cache.v1.';

/**
 * Removes every cached KB / search-engine list persisted in
 * sessionStorage. Called by both `AuthService.logout()` and
 * `AuthService.handleSessionExpired()` so a new user signing in on the
 * same browser tab never inherits the previous user's cache.
 *
 * Exported (rather than a private method) so tests can drive it
 * directly without constructing AuthService — matches
 * `purgeReportDraftStorage` and `purgeRefSearchHistoryStorage`.
 *
 * Silent no-op when sessionStorage is unavailable (SSR / private-mode).
 */
export function purgeKnowledgeBaseCacheStorage(): void {
  if (typeof window === 'undefined') return;
  let storage: Storage | undefined;
  try {
    storage = window.sessionStorage;
  } catch {
    return;
  }
  if (!storage) return;

  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k && k.startsWith(KB_CACHE_STORAGE_PREFIX)) keys.push(k);
  }
  for (const k of keys) storage.removeItem(k);
}

/**
 * Clears **all** of this origin's localStorage and sessionStorage. Called by
 * `AuthService.deleteAccount()` on successful deletion: the account no longer
 * exists, so every locally persisted trace of it (auth keys, per-user
 * preferences, drafts, caches, consent flags) goes with it — a key-by-key
 * purge would drift as new storage keys are added.
 *
 * Deliberately NOT used by `logout()` / `handleSessionExpired()`, which keep
 * cross-session conveniences (cookie consent, tour flags) intact.
 *
 * Exported (rather than a private method) so tests can drive it directly —
 * matches the sibling purge helpers above.
 *
 * Silent no-op per storage when unavailable (SSR / private-mode).
 */
export function purgeAllBrowserStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.clear();
  } catch {
    /* unavailable — nothing to clear */
  }
  try {
    window.sessionStorage?.clear();
  } catch {
    /* unavailable — nothing to clear */
  }
}

/**
 * Pure resolver behind {@link AuthService.activeSubscription}: the
 * allowed-subscriptions entry matching the user's currently-active
 * subscription id, or null when there is no match (profile not loaded yet,
 * or a stale id). Exported so specs exercise the real logic.
 */
export function resolveActiveSubscription(user: UserToken | null): UserAllowedSubscription | null {
  const subs = user?.allowedSubscriptions ?? [];
  return subs.find((s) => s.subscriptionId === user?.currentSubscription) ?? null;
}

/**
 * Pure resolver behind {@link AuthService.activeUsage}: consumption figures
 * for the active subscription, falling back to the user-level wallet when the
 * active plan is not metered monthly (Free/PAYG or allowance not set). In
 * wallet mode the bar's 100% is owned balance + lifetime consumed, so the bar
 * fills as the user spends and empties on top-up. `reserved` is the wallet's
 * in-flight hold in both modes (2026-07-01 usage-meter spec, decision 6).
 * Exported so specs exercise the real logic.
 */
export function resolveActiveUsage(
  user: UserToken | null,
  active: UserAllowedSubscription | null = resolveActiveSubscription(user),
): ActiveSubscriptionUsage {
  const reserved = user?.reservedCredits ?? 0;
  const availableCredits = user?.availableCredits ?? 0;
  const consumedCredits = user?.consumedCredits ?? 0;
  // The ACTIVE plan's own spendable balance (its pool + the personal wallet;
  // per-subscription pools, 2026-07-21). Plans never sum with each other.
  // Undefined = backend sent no per-plan figures → user-level wallet fallback.
  const available = active?.availableTokens ?? availableCredits;
  // Credit figures (C7, backend-authoritative integers). No active entry →
  // personal wallet fallback. In wallet mode (no monthly credit allowance)
  // the display allowance is synthesized as available + consumed so the
  // meter's 100% keeps its meaning.
  const creditsAvailable = active?.creditsAvailable ?? user?.creditBalance ?? 0;
  const creditsConsumed = active?.creditsConsumedThisPeriod ?? 0;
  // In-flight hold on the ACTIVE plan (integer credits, backend-derived).
  // Display-only: creditsAvailable is already NET of it server-side.
  const creditsReserved = active?.creditsReserved ?? 0;
  // Meter-headline split (2026-08-04): the headline leads with the plan's
  // OWN pool; the personal wallet surfaces as a separate additive note
  // (derived, never re-fetched). Gates keep `creditsAvailable` (spendable).
  const planCreditsAvailable = active?.planCreditsAvailable ?? creditsAvailable;
  const planCreditsConsumed = active?.planCreditsConsumed ?? creditsConsumed;
  const walletCreditsAvailable = Math.max(0, creditsAvailable - planCreditsAvailable);
  const creditAllowance =
    active && (active.monthlyCreditAllowance ?? 0) > 0
      ? (active.monthlyCreditAllowance as number)
      : planCreditsAvailable + planCreditsConsumed;
  if (active && active.monthlyAllowance > 0) {
    return {
      allowance: active.monthlyAllowance,
      consumed: active.consumedThisPeriod,
      reserved,
      available,
      resetDate: active.periodResetDate,
      source: 'monthly',
      creditAllowance,
      creditsAvailable,
      creditsConsumed,
      creditsReserved,
      planCreditsAvailable,
      walletCreditsAvailable,
    };
  }
  if (active?.availableTokens !== undefined) {
    const consumed = active.consumedTokens ?? 0;
    return {
      allowance: active.availableTokens + consumed,
      consumed,
      reserved,
      available,
      resetDate: null,
      source: 'wallet',
      creditAllowance,
      creditsAvailable,
      // Wallet mode meters the plan's own pool: lifetime credits drained
      // from it, personal wallet excluded (falls back to the period figure
      // when the backend predates the plan-scoped fields).
      creditsConsumed: planCreditsConsumed,
      creditsReserved,
      planCreditsAvailable,
      walletCreditsAvailable,
    };
  }
  return {
    allowance: availableCredits + consumedCredits,
    consumed: consumedCredits,
    reserved,
    available,
    resetDate: null,
    source: 'wallet',
    creditAllowance,
    creditsAvailable,
    creditsConsumed,
    creditsReserved,
    planCreditsAvailable,
    walletCreditsAvailable,
  };
}

/**
 * Plain-language explanation of the personal wallet, shown behind the (i)
 * icon next to the meter's wallet note (2026-08-04 UX pass: the bare figure
 * read as unexplained extra credits). Single source for every meter surface.
 */
export const PERSONAL_WALLET_NOTE_DETAIL =
  'Extra credits in your personal wallet, separate from this plan’s own ' +
  'credits. They can be spent under any of your plans and are only used ' +
  'after the plan’s credits run out.';

/**
 * Pure resolver for the meter's personal-wallet note: "Plus N credits in
 * your personal wallet", or null when there is nothing to note. Shared by
 * the header meter and manage-subscriptions so the copy never drifts.
 */
export function resolveWalletNote(usage: ActiveSubscriptionUsage): string | null {
  const wallet = usage.walletCreditsAvailable;
  if (wallet <= 0) return null;
  return `Plus ${wallet.toLocaleString('en-US')} credit${wallet === 1 ? '' : 's'} in your personal wallet`;
}

/**
 * Pure resolver behind {@link AuthService.activePlanLabel}: display label of
 * the user's current plan. Prefers the active subscription, then the first
 * allowed entry, and — once the profile has loaded — falls back to
 * {@link NO_PLAN_LABEL}: the Free plan is an explicit subscription (its
 * synthetic `free` entry appears in `allowedSubscriptions` once activated),
 * so an authenticated user with zero entries has NO plan. While the profile
 * is still loading the label stays empty so a paid user never flashes a
 * wrong plan name. Exported so specs exercise the real logic.
 */
export function resolveActivePlanLabel(
  user: UserToken | null,
  profileLoaded: boolean,
  active: UserAllowedSubscription | null = resolveActiveSubscription(user),
): string {
  if (!user) return '';
  const current = active ?? (user.allowedSubscriptions ?? [])[0] ?? null;
  if (current) return subscriptionDisplayLabel(current);
  return profileLoaded ? NO_PLAN_LABEL : '';
}

/**
 * Pure resolver behind {@link AuthService.isFreePlan}: true when the loaded
 * profile's subscription entries are exactly the backend's synthetic
 * {@link FREE_SUBSCRIPTION_ID} entry — i.e. the user explicitly subscribed
 * the Free plan and nothing else. Zero entries is NOT the Free plan any
 * more: it means no plan at all (see {@link resolveHasNoPlan}). False while
 * the profile is loading, so consumers never act on a partial snapshot.
 * Exported so specs exercise the real logic.
 */
export function resolveIsFreePlan(user: UserToken | null, profileLoaded: boolean): boolean {
  if (!profileLoaded || !user) return false;
  const subs = user.allowedSubscriptions ?? [];
  return subs.length > 0 && subs.every((s) => s.subscriptionId === FREE_SUBSCRIPTION_ID);
}

/**
 * Pure resolver behind {@link AuthService.hasNoPlan}: true when the loaded
 * profile carries no subscription entries at all — the user never subscribed
 * any plan (the Free plan included; its synthetic entry would otherwise be
 * present). Gates every analysis/References entry point behind the
 * "subscribe a plan" dialog. False while the profile is loading, so a paid
 * user is never gated by a partial snapshot. Exported so specs exercise the
 * real logic.
 */
export function resolveHasNoPlan(user: UserToken | null, profileLoaded: boolean): boolean {
  if (!profileLoaded || !user) return false;
  return (user.allowedSubscriptions ?? []).length === 0;
}

/**
 * Pure resolver behind {@link AuthService.hasPaidPlan}: true when the loaded
 * profile carries at least one non-Free subscription entry. PAYG entries are
 * already filtered out by HIDDEN_SUBSCRIPTION_IDS, so any remaining non-`free`
 * entry is a paid plan. False while the profile loads (never gate paid users
 * on a partial snapshot). Drives the paid-only "Buy credits" CTA (C4).
 */
export function resolveHasPaidPlan(user: UserToken | null, profileLoaded: boolean): boolean {
  if (!profileLoaded || !user) return false;
  return (user.allowedSubscriptions ?? []).some((s) => s.subscriptionId !== FREE_SUBSCRIPTION_ID);
}

/**
 * Pure resolver behind {@link AuthService.freePlanConsumed}: true when the
 * loaded profile says this email's one-time Free benefit was consumed by a
 * previous (deleted) account (`free_plan_consumed`). Backend-driven only —
 * a zero balance never implies it. False while the profile is loading so no
 * user is mis-branded on a partial snapshot. Exported for specs.
 */
export function resolveFreePlanConsumed(user: UserToken | null, profileLoaded: boolean): boolean {
  if (!profileLoaded || !user) return false;
  return user.freePlanConsumed === true;
}

/**
 * Message shape posted on the auth BroadcastChannel.
 *  - `logout`: a sibling tab signed out — the receiving tab clears state.
 *  - `profile-updated`: a sibling tab fetched a fresh profile (e.g. an
 *    updated credit balance) — the receiving tab re-fetches its own
 *    profile so the header/account balance stays in sync across tabs.
 */
interface AuthChannelMessage {
  type: 'logout' | 'profile-updated';
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  /** Current authenticated user, or null if not logged in */
  readonly user = signal<UserToken | null>(null);

  /** Whether the user is authenticated (has a valid, non-expired token) */
  readonly isAuthenticated = computed(() => {
    return this.user() !== null && this.isTokenValid();
  });

  /**
   * True once the initial `/users/get` has settled (success OR error) — or
   * when there was no session to load on boot. Guards that depend on the
   * full profile (`allowedSubscriptions`, subscription-level role, …) await
   * this signal on page refresh so they don't decide against the partial
   * rehydrated-from-storage snapshot.
   */
  readonly profileLoaded = signal<boolean>(false);

  /**
   * The allowed-subscriptions entry matching the user's currently-active
   * subscription (`currentSubscription`, persisted per-user in scoped
   * storage), or null when unknown. Every surface that needs "the active
   * subscription" should read this instead of re-deriving the match.
   */
  readonly activeSubscription = computed<UserAllowedSubscription | null>(() =>
    resolveActiveSubscription(this.user()),
  );

  /**
   * Consumption figures for the ACTIVE subscription — the single source for
   * every credits/usage display (header meter, account page, manage
   * subscriptions). Because it is a computed over the `user` signal, every
   * consumer updates automatically when `setCurrentSubscription` switches the
   * active subscription or when a profile refresh lands new numbers.
   * Resolution rules live in {@link resolveActiveUsage}.
   */
  readonly activeUsage = computed<ActiveSubscriptionUsage>(() =>
    resolveActiveUsage(this.user(), this.activeSubscription()),
  );

  /**
   * Display label of the user's current plan — the single source for every
   * plan-name display (header dropdown, account page, manage subscriptions).
   * Resolves to "Free" for users with no subscription entries once the
   * profile has loaded; empty while it is still loading. Resolution rules
   * live in {@link resolveActivePlanLabel}.
   */
  readonly activePlanLabel = computed<string>(() =>
    resolveActivePlanLabel(this.user(), this.profileLoaded(), this.activeSubscription()),
  );

  /**
   * Whether the user explicitly subscribed the Free plan (only the synthetic
   * `free` entry present). False until the profile loads. Gates
   * free-plan-only UI (welcome modal, free empty states) so paid users and
   * team-seat members are never misclassified. Rules live in
   * {@link resolveIsFreePlan}.
   */
  readonly isFreePlan = computed<boolean>(() =>
    resolveIsFreePlan(this.user(), this.profileLoaded()),
  );

  /**
   * Whether the user has NO plan at all — a fresh account that has not yet
   * subscribed the Free plan nor any paid one. Every analysis/References
   * entry point gates on this with the "subscribe a plan" dialog (CTA →
   * /pricing). False until the profile loads. Rules live in
   * {@link resolveHasNoPlan}.
   */
  readonly hasNoPlan = computed<boolean>(() => resolveHasNoPlan(this.user(), this.profileLoaded()));

  /** True when the user owns at least one paid plan entry. See {@link resolveHasPaidPlan}. */
  readonly hasPaidPlan = computed<boolean>(() =>
    resolveHasPaidPlan(this.user(), this.profileLoaded()),
  );

  /**
   * Whether this email's one-time Free benefit was consumed by a previous
   * account (backend `free_plan_consumed`). Drives the initial notice, the
   * `'free-used'` token gate, the account/subscriptions state and the meter
   * label. False until the profile loads. Rules live in
   * {@link resolveFreePlanConsumed}.
   */
  readonly freePlanConsumed = computed<boolean>(() =>
    resolveFreePlanConsumed(this.user(), this.profileLoaded()),
  );

  /**
   * Lazily-constructed Cognito user pool. Null when the environment
   * config is missing / still contains CI placeholders. In that case
   * the app still boots, but any auth call throws a clear error.
   */
  private userPool: CognitoUserPool | null = null;

  /** Reentrancy guard: prevents concurrent logouts (e.g. a burst of 401s). */
  private loggingOut = false;

  /** Optional cross-tab logout channel (null when BroadcastChannel unavailable). */
  private authChannel: BroadcastChannel | null = null;

  /**
   * Injector used to resolve `ReportPollingService` lazily on logout.
   * Eager injection here would create a circular DI cycle, because
   * `ReportPollingService` injects `AuthService` in its own constructor.
   */
  private readonly injector = inject(Injector);

  /** Eager — `ToastService` does not depend on `AuthService`. */
  private readonly toasts = inject(ToastService);

  /** Eager — `AnalyticsService` does not depend on `AuthService`. */
  private readonly analytics = inject(AnalyticsService);

  /**
   * Lazy accessor for `ReportPollingService`. See {@link injector}.
   */
  private get reportPolling(): ReportPollingService {
    return this.injector.get(ReportPollingService);
  }

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
    this.initUserPool();
    this.openAuthChannel();

    // On app boot, if we have a valid token, rehydrate the user signal
    // from storage IMMEDIATELY so the UI doesn't flash a logged-out state
    // while /users/get is in flight. `rehydrateUserFromStorage` mutates
    // the signal directly. After that, call loadUserProfile() to upgrade
    // with authoritative server values.
    if (this.isTokenValid()) {
      this.rehydrateUserFromStorage();

      this.loadUserProfile().subscribe({
        next: () => this.profileLoaded.set(true),
        error: () => {
          // 401 is already handled in loadUserProfile's catchError (tokens
          // cleared). For other errors, tokens stay and the rehydrated
          // user signal persists so the app remains usable. Either way the
          // initial load attempt has settled, so guards can proceed.
          this.profileLoaded.set(true);
        },
      });
    } else {
      // No session to load — guards can decide immediately.
      this.profileLoaded.set(true);
    }
  }

  /**
   * Rehydrates a minimal UserToken from scoped storage (em, name, csub).
   * Called in the constructor before /users/get so the header avatar,
   * email, and subscription picker render instantly on reload.
   */
  private rehydrateUserFromStorage(): void {
    const email = getScopedStorageItem(EMAIL_KEY);
    if (!email) return;
    const name = getScopedStorageItem(NAME_KEY) || '';
    // Don't fall back to a literal 'pay-as-you-go' here — that hardcoded
    // value used to bleed into the header dropdown on every fresh boot
    // (and stay sticky until loadUserProfile finished). Leave the field
    // blank until /users/get resolves; mapToUserToken below picks the
    // canonical id using the user's allowed_subscriptions.
    const csub = getScopedStorageItem(currentSubscriptionKey(email)) || '';
    this.user.set({
      email,
      displayName: name,
      role: 'user',
      subscriptions: [],
      currentSubscription: csub,
      availableCredits: 0,
      consumedCredits: 0,
      unreadNotificationsCount: 0,
      monthlyAllowance: 0,
      consumedThisPeriod: 0,
      periodResetDate: null,
      creditBalance: 0,
    });
  }

  // ── Email/Password Authentication ────────────────────

  /**
   * Signs in with email and password via Cognito SDK.
   * On success, stores the access token and loads the full user profile.
   * @param email - User's email address
   * @param password - User's password
   */
  signIn(email: string, password: string): Observable<UserToken> {
    return new Observable<string>((observer) => {
      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.requireUserPool(),
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (result) => {
          const accessToken = result.getAccessToken().getJwtToken();
          this.storeToken(accessToken, email);
          observer.next(accessToken);
          observer.complete();
        },
        onFailure: (err) => {
          console.error('[AuthService] Cognito signIn error:', err);
          observer.error(err);
        },
        newPasswordRequired: () => {
          console.warn(
            '[AuthService] Cognito requires new password (first login after admin create)',
          );
          observer.error({
            code: 'NewPasswordRequired',
            message: 'You need to set a new password. Please use the reset password page.',
          });
        },
      });
    }).pipe(
      switchMap(() => this.loadUserProfile()),
      tap(() => this.analytics.track({ event: 'login', method: 'email' })),
    );
  }

  /**
   * Registers a new user with Cognito. After success, user must verify their email.
   * @param email - User's email address
   * @param password - Password (min 8 chars, uppercase + number per Cognito defaults)
   * @param displayName - User's display name
   */
  signUp(email: string, password: string, displayName: string): Observable<ISignUpResult> {
    return new Observable<ISignUpResult>((observer) => {
      const attributes = [
        new CognitoUserAttribute({ Name: 'email', Value: email }),
        new CognitoUserAttribute({ Name: 'name', Value: displayName }),
      ];

      this.requireUserPool().signUp(email, password, attributes, [], (err, result) => {
        if (err) {
          console.error('[AuthService] Cognito signUp error:', err);
          observer.error(err);
          return;
        }
        observer.next(result!);
        observer.complete();
      });
    }).pipe(tap(() => this.analytics.track({ event: 'sign_up', method: 'email' })));
  }

  /**
   * Verifies the 6-digit code sent to the user's email after registration.
   * @param email - The email that was registered
   * @param code - 6-digit verification code
   */
  verifyCode(email: string, code: string): Observable<void> {
    return new Observable<void>((observer) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.requireUserPool(),
      });

      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) {
          observer.error(err);
          return;
        }
        observer.next();
        observer.complete();
      });
    }).pipe(tap(() => this.analytics.track({ event: 'sign_up_verified' })));
  }

  /**
   * Initiates the forgot-password flow. Cognito sends a reset code to the email.
   * @param email - The registered email address
   */
  forgotPassword(email: string): Observable<void> {
    return new Observable<void>((observer) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.requireUserPool(),
      });

      cognitoUser.forgotPassword({
        onSuccess: () => {
          observer.next();
          observer.complete();
        },
        onFailure: (err) => {
          observer.error(err);
        },
      });
    });
  }

  /**
   * Completes the password reset with the verification code and new password.
   * @param email - The registered email address
   * @param code - Verification code from email
   * @param newPassword - The new password
   */
  resetPassword(email: string, code: string, newPassword: string): Observable<void> {
    return new Observable<void>((observer) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.requireUserPool(),
      });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          observer.next();
          observer.complete();
        },
        onFailure: (err) => {
          observer.error(err);
        },
      });
    });
  }

  // ── Google OAuth ─────────────────────────────────────

  /**
   * Initiates Google Sign-in via Cognito Hosted UI.
   * Redirects the browser to the Cognito authorize endpoint with
   * `identity_provider=Google`. After Google auth, Cognito redirects
   * back to `${window.location.origin}/auth-callback.html` with `?code=…`.
   *
   * The redirect URI is derived at runtime from the current origin so the
   * same bundle works on localhost, dev.kirigami.ai, S3 preview deploys,
   * and www.kirigami.ai. Each origin must still be registered in the
   * Cognito app client's "Allowed callback URLs" list.
   *
   * `prompt=select_account` is forwarded to Google so the account chooser
   * always appears — without it, Google silently reuses the last signed-in
   * account and the user can't switch identities.
   */
  signInWithGoogle(): void {
    setScopedStorageItem('return_url', '/dashboard');
    const redirectUri = this.buildRedirectUri();
    const url =
      `https://${environment.cognito.domain}/oauth2/authorize?` +
      `identity_provider=Google&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=${environment.cognito.responseType}&` +
      `client_id=${environment.cognito.priClientId}&` +
      `scope=${encodeURIComponent(environment.cognito.scope)}&` +
      `prompt=select_account`;
    window.location.href = url;
  }

  /**
   * Builds the OAuth redirect URI from the current browser origin.
   * Exposed as a single method so tests and potential future flows can
   * share the exact same logic.
   */
  private buildRedirectUri(): string {
    const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
    return `${origin}/auth-callback.html`;
  }

  /**
   * Builds a Cognito Hosted UI logout URL that signs the user out and lands on
   * the `logout-callback.html` page for the current origin. Uses `logout_uri`
   * (not `redirect_uri`) so Cognito ends the session instead of initiating a
   * fresh login flow.
   *
   * Runs in every environment: the Hosted UI session cookie must be torn down
   * on logout so the next Google sign-in shows the account chooser again (see
   * `logout()` and `signInWithGoogle()`).
   *
   * `logout_uri` must EXACTLY match a URL registered in the app client's
   * "Allowed sign-out URLs". Every environment registers the
   * `logout-callback.html` page (the sign-out mirror of `auth-callback.html`,
   * which clears scoped storage and returns to `/`) — a bare origin would not
   * match, and Cognito then rejects the request with "Required parameters
   * missing" (it falls back to the redirect-mode logout and finds none of its
   * params). Keep this page name in sync with the Cognito configuration.
   */
  private buildHostedLogoutUrl(): string | null {
    const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
    if (!origin) return null;

    const logoutUri = `${origin}/logout-callback.html`;
    const params = new URLSearchParams({
      client_id: environment.cognito.priClientId,
      logout_uri: logoutUri,
    });

    return `https://${environment.cognito.domain}/logout?${params.toString()}`;
  }

  /**
   * Browser-level navigation helper used for hosted auth round-trips.
   * Kept as a separate method so tests can intercept the redirect cleanly.
   */
  private navigateBrowser(url: string): void {
    if (typeof window === 'undefined' || !window.location) return;
    window.location.href = url;
  }

  /**
   * Exchanges an OAuth authorization code for an application access token.
   * Called after Google sign-in when the callback stashes the code in storage.
   * @param code - The authorization code from Cognito
   */
  exchangeCodeForTokens(code: string): Observable<UserToken> {
    return this.http
      .get<{
        access_token?: string;
        email?: string;
        name?: string;
      }>(`${environment.apiBaseUrl}/auth/exchange-code?code=${code}`)
      .pipe(
        tap((data) => {
          this.storeToken(data.access_token || '', data.email || '', data.name || '');
        }),
        switchMap(() => this.loadUserProfile()),
        tap(() => this.analytics.track({ event: 'login', method: 'google' })),
        catchError((error) => {
          console.error('[AuthService] Code exchange failed:', error);
          this.clearTokens();
          return throwError(() => new Error('Google sign-in failed. Please try again.'));
        }),
      );
  }

  /**
   * Checks for a pending OAuth code in storage (set by auth-callback.html)
   * and exchanges it. Called once at app bootstrap.
   * @returns Observable that completes after exchange, or immediately if no code.
   */
  consumeAuthCode(): Observable<UserToken | null> {
    const code = getScopedStorageItem('auth_code');
    if (!code) {
      return new Observable((observer) => {
        observer.next(null);
        observer.complete();
      });
    }
    // Clear immediately to prevent re-processing on subsequent reloads
    removeScopedStorageItem('auth_code');
    return this.exchangeCodeForTokens(code);
  }

  // ── User Profile ─────────────────────────────────────

  /**
   * Fetches the authenticated user's profile from the backend.
   * Called after sign-in and on app boot if a valid token exists.
   *
   * On a successful refresh it broadcasts a `profile-updated` event so
   * sibling tabs re-sync their balance/header. The cross-tab handler that
   * reacts to that event calls this with `{ broadcast: false }` so the
   * receiver does NOT rebroadcast — otherwise the tabs would ping-pong
   * `profile-updated` messages forever.
   *
   * @param opts.broadcast - When `false`, suppresses the cross-tab
   *   `profile-updated` broadcast on success. Defaults to `true`.
   * @returns Observable emitting the UserToken
   */
  loadUserProfile(opts: { broadcast?: boolean } = {}): Observable<UserToken> {
    const broadcast = opts.broadcast ?? true;
    return this.http.get<UserProfileApiResponse>(`${environment.apiBaseUrl}/users/get`).pipe(
      map((response) => this.mapToUserToken(response)),
      tap((user) => {
        this.user.set(user);
        const sub = this.cognitoSub();
        if (sub) this.analytics.identify(sub, { planType: this.planTypeOf(user), role: user.role });
        // Persist the authoritative email + display name so a subsequent
        // refresh can rehydrate the `user` signal from storage before
        // /users/get returns. Without the email, `rehydrateUserFromStorage`
        // bails early and the signal stays `null` until the network call
        // resolves — the condition the authGuard now tolerates by waiting,
        // but persisting `em` restores the instant-render fast path too.
        if (user.email) setScopedStorageItem(EMAIL_KEY, user.email);
        if (user.displayName) setScopedStorageItem(NAME_KEY, user.displayName);
        // Tell sibling tabs to re-sync (e.g. an updated credit balance).
        // Skipped when this refresh was itself triggered by a sibling's
        // `profile-updated` message, to avoid an infinite rebroadcast loop.
        if (broadcast) this.broadcastProfileUpdated();
      }),
      catchError((error) => {
        // Only clear tokens on a definitive session expiry (401). Transient
        // errors (0/500/CORS/etc.) keep tokens so the user isn't logged out
        // by backend blips — any real 401 later will be caught by
        // auth-error.interceptor.ts and trigger a proper logout.
        if (error?.status === 401) {
          console.warn('[AuthService] /users/get returned 401; clearing session.');
          this.clearTokens();
          this.user.set(null);
          this.analytics.clearIdentity();
        } else {
          console.warn('[AuthService] /users/get failed (keeping session):', error);
        }
        return throwError(() => new Error('Failed to load user profile.'));
      }),
    );
  }

  /**
   * Explicitly subscribes the authenticated user to the Free plan
   * (POST /subscriptions/free) and reloads the profile so the synthetic
   * `free` entry lands in `allowedSubscriptions` (flipping `isFreePlan` on
   * and `hasNoPlan` off) before consumers navigate. Idempotent server-side:
   * repeated calls never re-grant the free token allowance.
   */
  subscribeFreePlan(): Observable<{ grantSkipped: boolean }> {
    return this.http
      .post<FreePlanActivationResponse>(`${environment.apiBaseUrl}/subscriptions/free`, {})
      .pipe(
        switchMap((res) =>
          this.loadUserProfile().pipe(
            map(() => {
              // Denied one-time grant: the explicit flag when the backend
              // ships it, else the 2026-07-17 signal (activated + 0 tokens).
              // `already_active` carries no granted_tokens → false.
              const grantSkipped =
                res.free_plan_consumed === true ||
                (res.status === 'activated' && res.granted_tokens === 0);
              this.analytics.track({ event: 'free_plan_activated', grant_skipped: grantSkipped });
              return { grantSkipped };
            }),
          ),
        ),
      );
  }

  // ── Account Management ────────────────────────────────

  /**
   * Updates the user's display name via the backend API.
   * Also refreshes the local user state so the header updates immediately.
   * @param displayName - The new display name
   */
  updateProfile(displayName: string): Observable<void> {
    const email = this.user()?.email;
    if (!email) return throwError(() => new Error('Not authenticated.'));

    return this.http
      .put<void>(`${environment.apiBaseUrl}/users/${email}`, {
        display_name: displayName,
      })
      .pipe(
        tap(() => {
          // Update local state immediately
          const current = this.user();
          if (current) {
            this.user.set({ ...current, displayName });
            setScopedStorageItem(NAME_KEY, displayName);
          }
        }),
        catchError((error) => {
          console.error('[AuthService] Failed to update profile:', error);
          return throwError(() => new Error('Failed to update profile.'));
        }),
      );
  }

  /**
   * Records the user's acceptance of the current General Terms & Conditions.
   * Posts to the dedicated acceptance endpoint; on a confirmed 200 it updates
   * the local `user` signal so the terms gate closes reactively. On failure
   * the error propagates and the gate stays open (no optimistic hide).
   * @param version - The accepted terms version (CURRENT_TERMS_VERSION)
   */
  acceptGeneralTerms(version: string): Observable<void> {
    const email = this.user()?.email;
    if (!email) return throwError(() => new Error('Not authenticated.'));

    return this.http
      .post<void>(`${environment.apiBaseUrl}/users/${email}/terms-acceptance`, {
        terms_type: 'general',
        version,
      })
      .pipe(
        tap(() => {
          const current = this.user();
          if (current) {
            this.user.set({ ...current, acceptedVersionOfGeneralTerms: version });
          }
        }),
        catchError((error) => {
          console.error('[AuthService] Failed to record terms acceptance:', error);
          return throwError(() => new Error('Failed to record terms acceptance.'));
        }),
      );
  }

  /**
   * Changes the user's password via the Cognito SDK.
   * Requires the current (old) password for verification.
   * @param oldPassword - Current password
   * @param newPassword - New password
   */
  changePassword(oldPassword: string, newPassword: string): Observable<void> {
    return new Observable<void>((observer) => {
      const email = this.user()?.email || getScopedStorageItem(EMAIL_KEY);
      if (!email) {
        observer.error(new Error('Not authenticated.'));
        return;
      }

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.requireUserPool(),
      });

      // Cognito requires an authenticated session to change password.
      // Re-authenticate with the old password first, then change it.
      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: oldPassword,
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: () => {
          cognitoUser.changePassword(oldPassword, newPassword, (err) => {
            if (err) {
              console.error('[AuthService] Failed to change password:', err);
              observer.error(err);
              return;
            }
            observer.next();
            observer.complete();
          });
        },
        onFailure: (err) => {
          console.error('[AuthService] Auth failed for password change:', err);
          observer.error(err);
        },
      });
    });
  }

  /**
   * Permanently deletes the user's account via the backend API, then logs out.
   */
  deleteAccount(): Observable<void> {
    const email = this.user()?.email;
    if (!email) return throwError(() => new Error('Not authenticated.'));

    return this.http.delete<void>(`${environment.apiBaseUrl}/users/${email}`).pipe(
      tap(() => {
        // The account no longer exists — wipe ALL of this origin's local
        // and session storage, not just the auth keys a regular logout
        // clears (per-user prefs, drafts, caches, consent flags included).
        purgeAllBrowserStorage();
        this.user.set(null);
        this.router.navigate(['/login']);
      }),
      catchError((error) => {
        console.error('[AuthService] Failed to delete account:', error);
        // Backend refuses deletion while the user owns an active subscription
        // (409 active_subscription_exists) — surface the cancel-first action
        // instead of the generic failure message.
        if (error?.status === 409 && error?.error?.code === 'active_subscription_exists') {
          return throwError(
            () => new Error('Cancel your active subscription before deleting your account.'),
          );
        }
        return throwError(() => new Error('Failed to delete account.'));
      }),
    );
  }

  // ── Logout ───────────────────────────────────────────

  /**
   * Logs the user out.
   *
   * Flow:
   *   1. Fire `GET /auth/logout` as fire-and-forget for any server-side
   *      session cleanup. We **ignore** its `logout_url` response — it
   *      is pre-built server-side with a hardcoded dev redirect that
   *      doesn't know about the current deployment origin.
   *   2. Clear local tokens and reset the user signal.
   *   3. Broadcast to other tabs so they log out too.
   *   4. Round-trip through Cognito Hosted UI `/logout` with a dynamic
   *      `logout_uri` for the current origin so the federated session ends too.
   *      If no browser origin is available, fall back to router navigation.
   *
   * Using Cognito's `logout_uri` parameter is important for Google SSO:
   * a local-only logout leaves the Hosted UI session cookie alive, so the
   * next sign-in can silently reuse the previous federated session and skip
   * the Google account chooser. Each supported origin must be registered in
   * the app client's "Allowed sign-out URLs" list.
   *
   * Idempotent — concurrent calls (e.g. a burst of 401s) collapse into one.
   */
  logout(): void {
    this.analytics.clearIdentity();

    if (this.loggingOut) return;
    this.loggingOut = true;

    // Fire-and-forget — we don't wait for the response and we don't use
    // `logout_url` from it (see method JSDoc).
    this.http.get<LogoutResponse>(`${environment.apiBaseUrl}/auth/logout`).subscribe({
      error: () => {
        /* backend unavailable — local cleanup still happens */
      },
    });

    this.clearTokens();
    purgeReportDraftStorage();
    purgeRefSearchHistoryStorage();
    purgeKnowledgeBaseCacheStorage();
    this.reportPolling.stopAll();
    this.toasts.clear();
    this.user.set(null);
    this.broadcastLogout();

    const hostedLogoutUrl = this.buildHostedLogoutUrl();
    if (hostedLogoutUrl) {
      this.navigateBrowser(hostedLogoutUrl);
      return;
    }

    this.router.navigate(['/']);
  }

  /**
   * Handles a session-expired signal from the HTTP error interceptor.
   * Skips the backend `/auth/logout` call (the server already knows — the
   * session is expired) and redirects to /login preserving a returnUrl so
   * the user lands back on the page they were on after re-authenticating.
   *
   * Idempotent — concurrent 401s are collapsed into one logout.
   */
  handleSessionExpired(): void {
    this.analytics.clearIdentity();

    if (this.loggingOut) return;
    this.loggingOut = true;

    const returnUrl = this.router.url && this.router.url !== '/login' ? this.router.url : '';

    this.clearTokens();
    purgeReportDraftStorage();
    purgeRefSearchHistoryStorage();
    purgeKnowledgeBaseCacheStorage();
    this.reportPolling.stopAll();
    this.toasts.clear();
    this.user.set(null);
    this.broadcastLogout();

    const extras = returnUrl ? { queryParams: { returnUrl } } : {};
    this.router.navigate(['/login'], extras);
  }

  // ── Cognito pool init ────────────────────────────────

  /**
   * Constructs the Cognito user pool from environment config. If the
   * environment still has `__REPLACE_IN_CI__` placeholders (e.g. a prod
   * bundle served without CI substitution), logs an actionable error
   * and leaves `userPool` null. The app still boots; auth calls throw
   * a friendly error via `requireUserPool()`.
   */
  private initUserPool(): void {
    const { userPoolId, pubClientId } = environment.cognito;
    if (!userPoolId || userPoolId.startsWith('__REPLACE_IN_CI__')) {
      console.error(
        '[AuthService] Cognito pool not configured: `userPoolId` still contains a ' +
          'CI placeholder. This prod bundle needs its CI/CD secret-substitution step. ' +
          'For local testing of a prod-optimized build, use ' +
          '`nx serve kirigami-app --configuration=staging` instead.',
      );
      return;
    }
    try {
      this.userPool = new CognitoUserPool({
        UserPoolId: userPoolId,
        ClientId: pubClientId,
      });
    } catch (error) {
      console.error(
        '[AuthService] Failed to construct Cognito user pool. Check ' +
          'environment.cognito.userPoolId / pubClientId values.',
        error,
      );
    }
  }

  /**
   * Updates the currently-active subscription on the user signal and
   * persists it in scoped storage so it survives reloads.
   * @param subscriptionId - The subscription ID to activate
   */
  setCurrentSubscription(subscriptionId: string): void {
    const current = this.user();
    if (!current) return;
    this.user.set({ ...current, currentSubscription: subscriptionId });
    setScopedStorageItem(currentSubscriptionKey(current.email), subscriptionId);
  }

  /**
   * Returns the user pool, throwing a friendly error when the pool was
   * never constructed (misconfigured environment). Every Cognito method
   * goes through this so the error surfaces at the point of use.
   */
  private requireUserPool(): CognitoUserPool {
    if (!this.userPool) {
      throw new Error(
        'Authentication is not configured in this build. ' +
          'If you are a developer, switch to the staging or development configuration.',
      );
    }
    return this.userPool;
  }

  // ── Cross-tab sync ───────────────────────────────────

  /** Open the BroadcastChannel if supported. Best-effort — no-op on failure. */
  private openAuthChannel(): void {
    if (typeof BroadcastChannel === 'undefined') return;
    try {
      this.authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
      this.authChannel.onmessage = (event) => {
        const msg = event.data as AuthChannelMessage | null;
        if (msg?.type === 'logout') {
          // Another tab logged out — clear local state and redirect.
          // Do NOT re-broadcast (prevents ping-pong loops).
          this.analytics.clearIdentity();
          if (this.loggingOut) return;
          this.loggingOut = true;
          this.clearTokens();
          this.reportPolling.stopAll();
          this.toasts.clear();
          this.user.set(null);
          this.router.navigate(['/login']);
        } else if (msg?.type === 'profile-updated') {
          // Another tab refreshed its profile (e.g. a changed credit
          // balance) — re-fetch ours so the header/account balance stays
          // in sync. Pass `broadcast: false` so this receive-triggered
          // refresh does NOT rebroadcast and start an infinite loop.
          // Silent on error: a transient /users/get failure here must not
          // surface anything to the user (this tab's state simply stays
          // as-is until its own next refresh).
          if (this.loggingOut || !this.isTokenValid()) return;
          this.loadUserProfile({ broadcast: false }).subscribe({
            error: () => {
              /* transient refresh failure — keep current state, stay silent */
            },
          });
        }
      };
    } catch {
      this.authChannel = null;
    }
  }

  /** Emit a logout event to other tabs. No-op if channel is unavailable. */
  private broadcastLogout(): void {
    try {
      this.authChannel?.postMessage({ type: 'logout' } satisfies AuthChannelMessage);
    } catch {
      /* channel unavailable — silently ignore */
    }
  }

  /**
   * Emit a profile-updated event to other tabs so they re-sync their
   * balance/header. No-op if the channel is unavailable. Only posted on a
   * locally-initiated profile refresh — never in response to a received
   * `profile-updated` message (see {@link loadUserProfile}).
   */
  private broadcastProfileUpdated(): void {
    try {
      this.authChannel?.postMessage({ type: 'profile-updated' } satisfies AuthChannelMessage);
    } catch {
      /* channel unavailable — silently ignore */
    }
  }

  // ── Notification Counter ─────────────────────────────

  /**
   * Decrements the unread notification count on the local user signal.
   * Call this after a successful markAsRead / approveShare / rejectShare
   * so the header badge reflects the change without a full profile refresh.
   * Floors at zero — no-op if already at zero or user is not logged in.
   */
  decrementUnreadCount(): void {
    const current = this.user();
    if (!current || current.unreadNotificationsCount <= 0) return;
    this.user.set({
      ...current,
      unreadNotificationsCount: current.unreadNotificationsCount - 1,
    });
  }

  // ── Token Management ─────────────────────────────────

  /**
   * Returns the stored access token, or null if not present.
   */
  getAccessToken(): string | null {
    return getScopedStorageItem(TOKEN_KEY);
  }

  /**
   * Checks if the stored JWT access token is still valid (not expired).
   * Allows 30 seconds of clock skew.
   */
  isTokenValid(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp;
      if (!exp) return false;
      // Allow 30s clock skew (same as legacy)
      return exp > Date.now() / 1000 + 30;
    } catch {
      return false;
    }
  }

  // ── Private Helpers ──────────────────────────────────

  /** Stores the access token and basic user info via scoped storage. */
  private storeToken(accessToken: string, email: string, name?: string): void {
    setScopedStorageItem(TOKEN_KEY, accessToken);
    setScopedStorageItem(EMAIL_KEY, email);
    if (name) setScopedStorageItem(NAME_KEY, name);
    // A fresh successful auth ends any prior logout cycle in this app
    // instance. Google SSO masks this via a full-page redirect (fresh
    // service, flag defaults to false), but email signIn keeps the same
    // Angular instance — so a stale `true` from an earlier logout,
    // handleSessionExpired, or cross-tab broadcast would otherwise make
    // the next logout() no-op until the user refreshes the browser.
    this.loggingOut = false;
  }

  /** Clears all auth-related storage keys. */
  private clearTokens(): void {
    // Capture the email before removing EMAIL_KEY so we can clear this user's
    // per-user subscription key. Also clear the legacy un-scoped `csub` key to
    // tidy up any value written before per-user scoping existed.
    const email = this.user()?.email || getScopedStorageItem(EMAIL_KEY);
    removeScopedStorageItem(currentSubscriptionKey(email));
    removeScopedStorageItem(CSUB_KEY);
    removeScopedStorageItem(TOKEN_KEY);
    removeScopedStorageItem(EMAIL_KEY);
    removeScopedStorageItem(NAME_KEY);
  }

  /** Cognito `sub` from the stored access-token JWT; null when unparseable. */
  private cognitoSub(): string | null {
    const token = this.getAccessToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return typeof payload.sub === 'string' ? payload.sub : null;
    } catch {
      return null;
    }
  }

  /** Coarse plan tier for analytics — never the raw subscription id. */
  private planTypeOf(user: UserToken): PlanType {
    const sub = user.currentSubscription;
    if (!sub) return 'none';
    if (sub === 'free') return 'free';
    if (sub.startsWith('pilot_')) return 'pilot';
    return 'paid';
  }

  /**
   * Maps the raw API response from GET /users/get to a typed UserToken.
   * The backend returns snake_case fields; this normalises them.
   */
  /**
   * Raw response shape from GET /users/get.
   * Uses snake_case as returned by the backend.
   */
  private mapToUserToken(response: UserProfileApiResponse): UserToken {
    const allowedSubscriptions: UserAllowedSubscription[] = (response.allowed_subscriptions ?? [])
      // Drop entries with no id and any UI-hidden subscription (e.g. PAYG). This
      // is the single chokepoint that hides them from every surface reading
      // `allowedSubscriptions`; PAYG billing logic (literal-id fallbacks) is
      // unaffected. See HIDDEN_SUBSCRIPTION_IDS.
      .filter((s) => !!s?.subscription_id && !HIDDEN_SUBSCRIPTION_IDS.has(s.subscription_id ?? ''))
      .map((s) => ({
        subscriptionId: s.subscription_id ?? '',
        role: s.role ?? '',
        owner: s.owner ?? '',
        status: s.status ?? '',
        billingCycleInterval: s.billing_cycle_interval ?? '',
        nextBilledAt: s.next_billed_at ?? null,
        priceName: s.price_name ?? null,
        displayName: s.display_name ?? null,
        monthlyAllowance: s.monthly_allowance ?? 0,
        consumedThisPeriod: s.consumed_this_period ?? 0,
        periodResetDate: s.period_reset_date ?? null,
        // Per-plan token pool figures stay undefined when the backend omits
        // them, so resolveActiveUsage can fall back to the user-level wallet
        // instead of mistaking "no data" for a zero balance.
        availableTokens: s.available_tokens,
        consumedTokens: s.consumed_tokens,
        // In-flight holds (2026-07-22): available figures above are already
        // NET of these — they are display-only, never meter inputs.
        reservedTokens: s.reserved_tokens,
        // C7 credit figures also stay undefined when the backend omits them
        // (pre-credits deployment): coercing to 0 here turned "no data" into
        // "zero balance" and false-gated every cost-bearing action.
        monthlyCreditAllowance: s.monthly_credit_allowance,
        creditsAvailable: s.credits_available,
        creditsConsumedThisPeriod: s.credits_consumed_this_period,
        creditsReserved: s.credits_reserved,
        // Plan-scoped mirrors (2026-08-04): the plan's own pool only — the
        // meter headline. Personal wallet excluded server-side.
        planCreditsAvailable: s.plan_credits_available,
        planCreditsConsumed: s.plan_credits_consumed,
      }));

    // Resolution order:
    //   1. The backend's authoritative current subscription (accept both
    //      snake_case — current API — and the legacy camelCase variant);
    //   2. A previously-chosen subscription persisted in scoped storage,
    //      but only if it still appears in the user's allowed list (a
    //      stale id from a since-revoked subscription must not bleed
    //      through to the dropdown);
    //   3. The first `active` allowed subscription;
    //   4. The first allowed subscription, whatever its status;
    //   5. Empty — no current subscription. The header dropdown shows
    //      nothing selected, which is the honest UI state in that case.
    // Resolve the user's email once — used both to scope the persisted
    // subscription choice (per-user key) and as the token's email below.
    const email = response.email || getScopedStorageItem(EMAIL_KEY) || '';
    const isKnown = (id: string): boolean =>
      allowedSubscriptions.some((s) => s.subscriptionId === id);
    const backendChoice = response.current_subscription || response.currentSubscription || '';
    const storedChoice = getScopedStorageItem(currentSubscriptionKey(email)) || '';
    const firstActive =
      allowedSubscriptions.find((s) => (s.status || '').toLowerCase() === 'active')
        ?.subscriptionId || '';
    const firstAllowed = allowedSubscriptions[0]?.subscriptionId || '';
    // A remembered choice of the synthetic Free plan yields to an active PAID
    // subscription: "free" is only ever auto-persisted while Free is the sole
    // plan (it is not selectable once a paid plan exists), so keeping it
    // pinned after a purchase would meter the user against the Free pool
    // instead of the plan they just bought. Paid entries precede the
    // synthetic ones in `allowedSubscriptions`, so `firstActive` then picks
    // the purchased plan.
    const hasActivePaid = allowedSubscriptions.some(
      (s) =>
        (s.status || '').toLowerCase() === 'active' && s.subscriptionId !== FREE_SUBSCRIPTION_ID,
    );
    const holds = (id: string): boolean =>
      isKnown(id) && !(id === FREE_SUBSCRIPTION_ID && hasActivePaid);
    const currentSubscription =
      (backendChoice && holds(backendChoice) ? backendChoice : '') ||
      (storedChoice && holds(storedChoice) ? storedChoice : '') ||
      firstActive ||
      firstAllowed ||
      '';

    // Persist the resolved current subscription (per-user key) so the next
    // boot can rehydrate it without cross-user contamination.
    if (currentSubscription) {
      setScopedStorageItem(currentSubscriptionKey(email), currentSubscription);
    } else {
      removeScopedStorageItem(currentSubscriptionKey(email));
    }

    return {
      email,
      displayName: response.display_name || response.name || getScopedStorageItem(NAME_KEY) || '',
      role: response.role || 'user',
      subscriptions: response.subscriptions || [],
      currentSubscription,
      availableCredits: response.available_credits ?? 0,
      consumedCredits: response.consumed_credits ?? 0,
      reservedCredits: response.reserved_credits ?? 0,
      monthlyAllowance: response.monthly_allowance ?? 0,
      consumedThisPeriod: response.consumed_this_period ?? 0,
      periodResetDate: response.period_reset_date ?? null,
      creditBalance: response.credit_balance ?? 0,
      unreadNotificationsCount: response.unread_notifications_count ?? 0,
      allowedSubscriptions,
      acceptedVersionOfGeneralTerms: response.terms_general_version,
      acceptedVersionOfRunAnalysisTerms: response.terms_run_analysis_version,
      freePlanConsumed: response.free_plan_consumed === true,
      restrictionReason: response.restriction_reason ?? null,
    };
  }
}
